// Онбординг при первом входе: короткий гайд по разделам с учётом
// роли. Показывается один раз — отметка хранится в localStorage.

import { useMemo, useState } from "react";
import { MeResponse } from "../types";

const STORAGE_KEY = "onboarding_done_v1";

export function onboardingNeeded(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "1";
  } catch {
    return false;
  }
}

interface Step {
  icon: string;
  title: string;
  text: string;
}

function buildSteps(me: MeResponse): Step[] {
  const steps: Step[] = [
    {
      icon: "👋",
      title: "Добро пожаловать",
      text:
        "Это рабочее мини-приложение. Внизу — основные разделы; " +
        "раздел «Ещё» открывает поиск, аналитику и профиль.",
    },
  ];
  if (me.permissions.payments_view) {
    steps.push({
      icon: "💳",
      title: "Реестр платежей",
      text:
        "Здесь заявки, ожидающие оплаты и подтверждения. Кнопка " +
        "«Выбрать» включает массовые операции — обработать несколько " +
        "заявок разом.",
    });
  }
  if (me.permissions.fx_view) {
    steps.push({
      icon: "💱",
      title: "Обмен валюты",
      text:
        "Разделы сгруппированы в кластеры «Обмен», «Крипта», «Учёт». " +
        "Частые маршруты можно сохранять как шаблоны и запускать в " +
        "один тап.",
    });
  }
  steps.push({
    icon: "✅",
    title: "Мои задачи",
    text:
      "Стартовый экран с тем, что требует вашего внимания. Списки " +
      "обновляются жестом «потянуть вниз».",
  });
  if (me.roles.includes("admin")) {
    steps.push({
      icon: "🩺",
      title: "Для администратора",
      text:
        "В «Ещё» доступны управление сотрудниками и «Состояние " +
        "системы» — статус планировщика, бэкапов и крипто-мониторинга.",
    });
  }
  return steps;
}

export function Onboarding({
  me,
  onDone,
}: {
  me: MeResponse;
  onDone: () => void;
}) {
  const steps = useMemo(() => buildSteps(me), [me]);
  const [idx, setIdx] = useState(0);

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* приватный режим — просто закрываем */
    }
    onDone();
  };

  const step = steps[idx];
  const last = idx === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-center justify-center"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      <div className="bg-tg-bg w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-5">
        <div className="text-4xl text-center">{step.icon}</div>
        <div className="text-lg font-semibold text-center mt-2 text-tg-text">
          {step.title}
        </div>
        <div className="text-sm text-tg-hint text-center mt-1.5 leading-relaxed">
          {step.text}
        </div>

        <div className="flex justify-center gap-1.5 mt-4">
          {steps.map((_, i) => (
            <span
              key={i}
              className={
                "w-1.5 h-1.5 rounded-full " +
                (i === idx ? "bg-tg-button" : "bg-tg-secondaryBg")
              }
            />
          ))}
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={finish}
            className="px-4 py-2.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            Пропустить
          </button>
          <button
            onClick={() => (last ? finish() : setIdx(idx + 1))}
            className="flex-1 px-4 py-2.5 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium"
          >
            {last ? "Начать работу" : "Далее"}
          </button>
        </div>
      </div>
    </div>
  );
}
